import { HStack, Icon, IconButton, Spinner, Text, VStack } from '@chakra-ui/react'
import {
  UploadDocumentButton,
  checkFileSize,
  checkValidExtension,
  uploadDocumentFile,
} from '@src/components/sco/UploadDocumentButton'
import { scoBackendUrl } from '@src/lib/startups-url.ts'
import React, { useRef, useState } from 'react'
import { LuFileCheck, LuUpload, LuX } from 'react-icons/lu'

export type DeckUploadCardProps = {
  fileLabel?: string | null
  /** Caption under the file name, e.g. "11.6 MB" or "Sample Deck Loaded". */
  fileSubLabel?: string | null
  onClear: () => void
  onError: (err: any) => void
  onUploaded: (url: string, storedFileId?: string) => void
}

export const DeckUploadCard: React.FC<DeckUploadCardProps> = ({
  fileLabel,
  fileSubLabel,
  onClear,
  onError,
  onUploaded,
}) => {
  const uploadButtonRef = useRef<HTMLButtonElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  const handleUploadAreaClick = (e: React.MouseEvent) => {
    // If the user clicked directly on the inner "Choose a PDF" button (or any button),
    // let its own onClick handler manage the file picker to avoid double-triggering.
    const target = e.target as HTMLElement
    if (target.closest('button')) {
      return
    }
    uploadButtonRef.current?.click()
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files[0]
    if (!file) {
      return
    }

    const allowedExtensions = ['.pdf']
    const maxSizeInBytes = 40000000

    if (!checkValidExtension({ allowedExtensions, fileToUpload: file })) {
      onError(`Please select a PDF file.`)
      return
    }

    if (!checkFileSize({ fileToUpload: file, maxSizeInBytes })) {
      onError(`Please select a file that is less than ${maxSizeInBytes / 1000000}MB in size.`)
      return
    }

    setIsUploading(true)

    try {
      await uploadDocumentFile({
        file,
        uploadUrl: scoBackendUrl('/assets/upload-pitch-deck'),
        storedFile: { attachmentType: 'PitchDeck', contextKey: 'original' },
        maxSizeInBytes,
        onSuccess: (url, storedFileId) => {
          onUploaded(url, storedFileId)
        },
        onError: err => {
          onError(err)
        },
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <VStack
      _hover={fileLabel || isUploading ? undefined : { borderColor: 'orange.solid' }}
      bgColor={isDragging ? 'colorPalette.card/50' : 'colorPalette.card/25'}
      borderColor={isDragging ? 'orange.solid' : fileLabel ? 'orange.solid/50' : 'orange.solid/25'}
      borderRadius={'xl'}
      borderStyle={'dashed'}
      borderWidth={'2px'}
      cursor={fileLabel ? undefined : 'pointer'}
      gap={2}
      paddingX={4}
      paddingY={fileLabel ? 5 : 6}
      textAlign={'center'}
      transition={'all 0.15s ease'}
      width={'full'}
      onClick={fileLabel ? undefined : handleUploadAreaClick}
      onDragEnter={!fileLabel ? handleDragEnter : undefined}
      onDragLeave={!fileLabel ? handleDragLeave : undefined}
      onDragOver={!fileLabel ? handleDragOver : undefined}
      onDrop={!fileLabel ? handleDrop : undefined}
    >
      {fileLabel ? (
        <HStack gap={3} maxWidth={'full'}>
          <Icon asChild boxSize={6} color={'teal.fg'} flexShrink={0}>
            <LuFileCheck />
          </Icon>
          <VStack align={'start'} gap={0} minWidth={0}>
            <Text truncate fontSize={'sm'} fontWeight={'600'} maxWidth={'full'}>
              {fileLabel}
            </Text>
            {fileSubLabel && (
              <Text color={'teal.fg'} fontSize={'xs'} fontWeight={'600'}>
                {fileSubLabel}
              </Text>
            )}
          </VStack>
          <IconButton
            aria-label={'Remove deck'}
            flexShrink={0}
            size={'xs'}
            variant={'outline'}
            onClick={onClear}
          >
            <LuX />
          </IconButton>
        </HStack>
      ) : isUploading ? (
        <VStack gap={2} py={2}>
          <Spinner color={'orange.solid'} size={'lg'} />
          <Text fontSize={'md'} fontWeight={'700'}>
            Uploading your Pitch Deck...
          </Text>
        </VStack>
      ) : (
        <>
          <Icon asChild boxSize={6} color={'orange.fg'}>
            <LuUpload />
          </Icon>
          <VStack gap={0}>
            <Text fontSize={'sm'} fontWeight={'700'}>
              {isDragging ? 'Drop your PDF here' : 'Upload your Pitch Deck'}
            </Text>
            <Text fontSize={'sm'} opacity={0.6}>
              {isDragging ? 'Release to upload' : 'PDF, up to 40MB'}
            </Text>
          </VStack>
          <UploadDocumentButton
            ref={uploadButtonRef}
            allowedExtensions={['.pdf']}
            size={'sm'}
            storedFile={{ attachmentType: 'PitchDeck', contextKey: 'original' }}
            uploadUrl={scoBackendUrl('/assets/upload-pitch-deck')}
            variant={'outline'}
            onError={onError}
            onSuccess={onUploaded}
          >
            Choose a PDF
          </UploadDocumentButton>
        </>
      )}
    </VStack>
  )
}
